<script setup lang="ts">
import { defineAsyncComponent } from "vue";

const url = useRequestURL();
const { data, error } = await useFetch<any>(
  `${url.origin}/content/index-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

const {tabs, content} = data.value;
useHead({
  title: data.value?.meta_tags?.title || "",
  meta: data.value?.meta_tags?.meta || [],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(data.value?.schema?.organization)
    }],
   link: [
    {
      rel: 'preload',
      as: 'image',
      href: '/images/grid.webp',
      type: 'image/webp',
     },   
      {
      rel: 'preload',
      as: 'image',
      href: '/images/wri-logo-v1.gif',
      type: 'image/webp',
     }, 
      {
      rel: 'preload',
      as: 'image',
      href: '/images/icons/icon-laravel-dark.svg',
      type: 'image/svg+xml',
     }, 
      {
      rel: 'preload',
      as: 'image',
      href: '/images/icons/Vue.webp',
      type: 'image/webp',
     }, 
      {
      rel: 'preload',
      as: 'image',
      href: '/images/vaahCmsDarkIcon.svg',
      type: 'image/svg+xml',
     }, 
  ] 
});

definePageMeta({
  layout: "home-layout",
});



const WrHero = defineAsyncComponent(
  () => import("~/components/molecules/WrHero.vue")
);
const LazyWrContactFormPopup = defineAsyncComponent(() => import('~/components/molecules/WrContactFormPopup.vue'));
const LazyHydrationWrAgile = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrAgile.vue'));
const LazyHydrationWrDeployment = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrDeployment.vue'));
const LazyHydrationWrAiServices = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrAiServices.vue'));
const LazyHydrationWrCmsSection = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrCmsSection.vue'));
const LazyHydrationWrTestimonialCardCarousel = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrTestimonialCardCarousel.vue'));  
const LazyHydrationWrTailored = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrTailored.vue'));
const LazyHydrationWrAnimatedIntroSection = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrAnimatedIntroSection.vue'));
const LazyHydrationWrContact = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrContact.vue'));
const LazyHydrationWrReelSection = defineLazyHydrationComponent('visible',() => import('~/components/molecules/WrReelSection.vue'));
const TabPanel = defineAsyncComponent(() => import("primevue/tabpanel"));
const Tabs = defineAsyncComponent(() => import("primevue/tabs"));
const TabList = defineAsyncComponent(() => import("primevue/tablist"));
const Tab = defineAsyncComponent(() => import("primevue/tab"));
const Card = defineAsyncComponent(() => import("primevue/card"));


const { isMobile } = useDevice();

const visible = ref(false);

const openModal = () => {
  visible.value = true;
};
</script>

<template>
  <div class="z-10 relative">
    <WrHero />
  </div>

  <!-- Reel Section: Building Intelligent Experiences with AI -->
  <div class="z-15 relative">
    <LazyHydrationWrReelSection :hydrate-on-visible="{ rootMargin: '100px' }" />
  </div>

  <div class="z-20 relative">
    <section class="relative z-20 overflow-hidden grid place-items-center pt-10 lg:pt-16">
      <div class="max-w-screen-xl mx-auto px-4 w-full">
        <h2 class="text-center mb-10">
          <WrGradientText
            text="Agile software development"
            append-class=" pb-0 lg:pb-4"
          />
          <p class="text-lg text-[var(--foreground)] tracking-[0.5px]">
            AI trained model on laravel, nuxt, vue and vaahcms
          </p>
        </h2>

        <div>
          <LazyHydrationWrAgile :hydrate-on-visible="{ rootMargin: '100px' }"/>

          <div class="w-full flex flex-col items-center p-4 mt-8 lg:mt-16">
            <h2
              class="font-semibold text-[var(--foreground)] text-lg md:text-2xl text-center mb-7"
            >
              Deployment
            </h2>
            <LazyHydrationWrDeployment :hydrate-on-visible="{ rootMargin: '100px' }"/>
          </div>
        </div>
      </div>
    </section>

    <!-- Revolutionising development with AI -->
    <section class="relative z-10 pb-10 lg:grid lg:place-items-center px-4">
      <div
        class="opacity-30 size-[1220px] rounded-full absolute left-1/2 -top-[87%] -translate-x-1/2 bg-[image:var(--bg-circle-spotlight)] shadow-lg"
      ></div>
      <div
        class="size-[790px] bg-[var(--background-glow)] blur-[200px] rounded-full absolute -top-[38%] left-1/2 -translate-x-1/2"
      ></div>
      <div class="relative z-20">
        <h2 class="text-center">
          <WrGradientText
            text="Revolutionising development with AI"
            append-class=" pb-0 lg:pb-4"
          />
          <p class="text-lg tracking-[0.5px] font-anvtgrade-book">
            Unlock new possibilities with AI-powered innovation.
            <br class="hidden lg:block" />
            Elevate efficiency, insights, and growth with our tailored solutions
          </p>
        </h2>
      </div>
      <div class="-my-10 lg:-mt-32 lg:-mb-48 relative z-10">
        <video autoplay loop muted playsinline width="1149">
          <source src="/images/ai.webm" type="video/mp4" />
        </video>
        <div
          v-if="$colorMode.preference != 'dark' && !isMobile"
          class="absolute top-20 left-1/2 -translate-x-1/2 w-1/2 h-40 rounded-full blur-[10px] z-10 bg-[linear-gradient(0deg,_#EAEAEA55_10%,_#EAEAEA_60%)] opaticity-50"
        ></div>
        <div
          v-if="$colorMode.preference != 'dark' && !isMobile"
          class="absolute bottom-20 left-1/2 -translate-x-1/2 w-1/2 h-40 rounded-full blur-[10px] z-10 bg-[linear-gradient(0deg,_#eaeaea00_40%,_#eaeaea_90%)] opaticity-50"
        ></div>
      </div>

      <LazyHydrationWrAiServices :hydrate-on-visible="{ rootMargin: '100px' }" />
      <WrButton link="/ai" container-class="z-10 relative mx-auto"
        >Explore more AI services</WrButton
      >
    </section>

    <section
      class="relative pt-10 lg:pt-20 overflow-visible grid place-items-center px-4"
    >
      <div
        v-if="$colorMode.preference == 'dark'"
        class="absolute w-[2266px] h-[1241px] top-20 rounded-[100%] bg-[linear-gradient(0deg,_#1F013405_31.11%,_#4D27B600_51.33%,_#855BF200_57.98%,_#22206D20_62.05%)] dark:bg-[linear-gradient(0deg,_#1F0134_31.11%,_#4D27B600_51.33%,_#855BF200_57.98%,_#22206D_62.05%)] blur-[100px] dark:blur-[100px]"
      >
        <div
          class="w-[2390px] h-[1863px] bg-white dark:bg-[var(--color-5)] rounded-[100%] absolute top-[200px] left-0"
        ></div>
      </div>
      <NuxtImg
        v-if="$colorMode.preference != 'dark'"
        src="/images/cms-back.webp"
        class="absolute -top-[15rem] left-1/2 -translate-x-1/2 min-w-[2950px] min-h-[2152px]"
        height="2152"
        width="2950"
        alt="cms-back"
        loading="lazy"
        format="webp"
      />

      <div class="rising-stars top-0">
        <div style="height: 4px; width: 4px"></div>
        <div style="height: 2px; width: 2px"></div>
        <div style="height: 1px; width: 1px"></div>
      </div>

      <div class="relative z-20">
        <h3 class="text-center">
          <p class="font-medium leading-tight">
            Use
           <WrSVGIcon name="WrVaahCms"  class="text-[var(--foreground)] inline-block"
              :width="isMobile ? 200 : 244"
              :height="isMobile ? 40 : 50"/>
            to build anything
          </p>
          <WrGradientText
            text="Or everything."
            append-class=" pt-0 pb-4 md:pb-8 "
          />
          <p
            class="text-lg text-[var(--foreground)] tracking-[0.5px] font-anvtgrade-book"
          >
            VaahCMS is a web application development platform
            <br class="hidden lg:block" />
            Built with Laravel, Vue with structured (HMVC) Modules & Themes.
          </p>
        </h3>

        <LazyHydrationWrCmsSection :hydrate-on-visible="{ rootMargin: '100px' }" />
      </div>
    </section>

    <section
      class="relative z-20 pt-10 lg:pt-20 overflow-hidden grid place-items-center max-w-screen-xl mx-auto px-4"
    >
      <h4 class="text-center">
        <WrGradientText text="Our products" append-class=" py-0 lg:pb-4" />
      </h4>
      <Tabs value="0" class="my-4 md:my-7">
        <TabList
          unstyled
          :pt="{
            tabList: {
              class:
                '*:selected_tab dark:aria-selected:*:text-white aria-selected:*:!text-[#501d71] dark:aria-selected:*:!text-white *:!text-[#838385] dark:*:!text-[#A64BE4] flex justify-center bg-transparent border-b-0',
            },
            activeBar: { class: 'hidden' },
          }"
        >
          <Tab unstyled v-for="tab in tabs" :key="tab.title" :value="tab.value">
            {{ tab.title }}
          </Tab>
        </TabList>
        

        <TabPanels unstyled class="bg-transparent pt-8">
          <TabPanel
            v-for="tab in tabs"
            :key="tab.value"
            :value="tab.value"
            unstyled
          >
            <div
              class="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-7"
            >
              <div
                v-for="(product, index) in tab.content"
                :key="index"
                class="bg-[image:var(--border-product-card)] rounded-xl p-px"
              >
                <Card
                  unstyled
                  class="p-4 md:p-7 bg-[var(--color-5)] text-[var(--primary)] text-sm h-full rounded-xl relative"
                >
                  <template #title>
                   <WrSVGIcon :name="product.svg" class="mb-4 text-[var(--foreground)] w-[90%] max-w-max"/>
                  </template>
                  <template #content>
                    <p class="m-0">{{ product.description }}</p>
                    <NuxtLink
                      :aria-label="product.label"
                      :to="product.link"
                      target="_blank"
                      class="absolute z-10 block size-full inset-0"
                    />
                  </template>
                </Card>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <WrButton @click="openModal">Schedule a Demo</WrButton>
    </section>

    <section
      class="relative pt-10 lg:pt-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20"
    >
      <h4 class="text-center mb-11">
        <WrGradientText text="Trusted for excellence and efficiency" />
        <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
          WebReinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h4>
      <!-- Carousel Section -->
      <LazyHydrationWrTestimonialCardCarousel class="mb-12" :hydrate-on-visible="{ rootMargin: '100px' }" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </section>

    <section
      class="relative pt-10 lg:pt-20 grid place-items-center z-10 max-w-screen-xl mx-auto px-4"
    >
      <div
        class="rotate-[0deg] rounded-full absolute size-[1200px] opacity-50 -top-[140%] -right-[25%] bg-[image:var(--bg-circle-tailored)]"
      ></div>
      <!-- <div
        class="rounded-full absolute retation-[75deg] size-[1220px] opacity-60 -top-52 -left-[35%] bg-[image:var(--bg-circle-tailored-sm)]">
      </div> -->
      <div class="relative z-10">
        <h5 class="text-center mb-8">
          <WrGradientText
            text="Tailored solutions for growth"
            append-class=" pb-4"
          />
          <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
            From Product Engineering to Mobile, CMS, API, and Scalable Business
            Growth.
          </p>
        </h5>
        <div class="relative">
          <div
            class="grid max-sm:max-w-[350px] mx-auto max-sm:place-content-left sm:grid-cols-2 lg:grid-cols-4 gap-x-2 gap-y-6"
          >
            <LazyHydrationWrTailored :content="content" :hydrate-on-visible="{ rootMargin: '100px' }"/>
          </div>

          <!-- <div class="max-sm:hidden flex justify-between pointer-events-none absolute top-0 w-full h-full">
            <div v-for="i in 6"
              class="h-full w-px bg-[#E1B8FF] dark:bg-[#1A2132] last:absolute last:top-1/2 last:w-full last:h-px last:dark:bg-[#1A2132]">
            </div>
          </div> -->
        </div>
        <WrButton
          container-class="max-w-max mx-auto mt-7 cursor-pointer"
          link="/services"
          >View all services</WrButton
        >
      </div>
    </section>

    <section class="py-10 lg:py-20 grid place-items-center relative">
      <div
        class="size-[818px] absolute -top-1/2 bg-[#47116B] blur-[240px] opacity-10 dark:opacity-50"
      ></div>
      <div class="relative z-10 w-full">
        <div
          class="opacity-50 rotate-[-60deg] size-[297px] rounded-full bg-[image:var(--bg-circle-contact)] left-1/2 -translate-x-[70%] top-[25%] absolute shadow-sm"
        ></div>
        <h5 class="text-center z-10 relative">
          <p class="text-lg font-anvtgrade-book">Want to collaborate?</p>
          <WrGradientText
            text="Let's have a chat"
            append-class="text-3xl lg:text-[48px] py-4 bg-[linear-gradient(92.15deg,_#12DBFF_35.57%,_#BA55FE_55.72%)]"
          />
        </h5>

        <div
          class="absolute -translate-x-1/2 text-center w-full lg:max-w-xl left-1/2 top-20 mt-5 px-4 z-30"
        >
          <LazyHydrationWrAnimatedIntroSection :hydrate-on-visible="{ rootMargin: '100px' }" />

          <WrButton
            container-class="max-w-max mx-auto mt-16 z-30 relative"
            @click="openModal"
            >Let's Connect</WrButton
          >
        </div>
        <LazyHydrationWrContact :hydrate-on-visible="{ rootMargin: '100px' }" />
      </div>
    </section>
  </div>

  <LazyWrContactFormPopup v-if="visible" :isOpen="visible" @close="visible = false" class="z-30" />
</template>
